import { Timer } from "./Timer.service";
import { VersionChecker } from "./VersionChecker.service";
import { VersionFetcher } from "./VersionFetcher.service";
import { UpdateUI } from "./UpdateUI.service";
import { UpdateServiceConfig } from "../types/update.types";
import { humanDurationToMs } from "../utils/durationToMs";
import {
  forceUrlToHaveTimestamp,
  reloadPageWithoutCache,
} from "../utils/reloadPage";

/**
 * Service responsible for managing application version updates and automatic page reloads.
 * It periodically checks for version changes and handles the update process.
 */
export class UpdateService {
  private readonly timer: Timer;
  private readonly versionChecker: VersionChecker;
  private readonly ui: UpdateUI;
  private readonly versionFetcher: VersionFetcher;
  private config: UpdateServiceConfig;

  /**
   * Creates a new UpdateService instance.
   * @param config - Configuration object containing version check settings and update behavior
   */
  constructor(config: UpdateServiceConfig) {
    this.config = this.normalizeConfig(config);

    this.versionFetcher = new VersionFetcher({
      versionFileUrl: this.config.versionFileUrl,
      currentVersion: this.config.currentVersion,
    });

    this.timer = new Timer();
    this.ui = new UpdateUI();
    this.versionChecker = new VersionChecker(this.versionFetcher, {
      checkInterval: this.config.checkInterval as number,
      checkImmediately: true,
    });
  }

  public setCurrentVersion(version: string): void {
    this.versionFetcher.setCurrentVersion(version);
  }

  /**
   * Starts the version checking process.
   * Initializes version mismatch detection and handling.
   * @returns Promise that resolves when the service has started
   */
  public async start(): Promise<void> {
    await this.versionChecker
      .onVersionMismatch(() => this.handleVersionMismatch())
      .start();
  }

  /**
   * Stops all active timers and version checking processes.
   */
  public stop(): void {
    this.versionChecker.stop();
    this.timer.stop();
  }

  /**
   * Handles the scenario when a version mismatch is detected.
   * Stops version checking and starts a countdown timer for page reload.
   * @private
   */
  private handleVersionMismatch(): void {
    this.versionChecker.stop();
    forceUrlToHaveTimestamp();

    this.timer.start({
      duration: this.config.timeForUpdateOrPageReload as number,
      onTimeLeftChange: (timeLeftInMs) => {
        const timeLeftInSeconds = Math.floor(timeLeftInMs / 1000);
        this.ui.displayBanner(this.config.messages, timeLeftInSeconds);
      },
      onComplete: reloadPageWithoutCache,
    });
  }

  /**
   * Normalizes the configuration by converting human-readable duration strings to milliseconds.
   * @param config - Raw configuration object
   * @returns Normalized configuration with durations in milliseconds
   * @private
   */
  private normalizeConfig(config: UpdateServiceConfig): UpdateServiceConfig {
    return {
      ...config,
      timeForUpdateOrPageReload: humanDurationToMs(
        config.timeForUpdateOrPageReload
      ),
      checkInterval: humanDurationToMs(config.checkInterval),
    };
  }
}
